"use client";

import React from "react";
import { Instagram, ArrowUpRight, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const galleryMoments = [{
  id: 1,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/community_tea_ritual.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/community_tea_ritual.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/community_tea_ritual.png-wvc-sizes",
  alt: "Preparación de té matutino con infusión de hongos Shiitake en taza de cerámica artesanal",
  handle: "@valeria.holistica",
  location: "CDMX",
  caption: "Mi infusión de Shiitake cada mañana antes de comenzar el día.",
  aspectClass: "md:col-span-7 aspect-[4/3] md:aspect-[16/10]"
}, {
  id: 2,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/community_skincare_window.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/community_skincare_window.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/community_skincare_window.png-wvc-sizes",
  alt: "Aplicación de crema facial Maitake junto a una ventana soleada",
  handle: "@sofiapielsaludable",
  location: "Guadalajara",
  caption: "Piel calmada y nutrida con la crema Maitake.",
  aspectClass: "md:col-span-5 aspect-[4/5]"
}, {
  id: 3,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/community_vanity_apothecary.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/community_vanity_apothecary.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/community_vanity_apothecary.png-wvc-sizes",
  alt: "Tocador de baño de estilo botánico con productos Spora ordenados",
  handle: "@camila_botanica",
  location: "Oaxaca",
  caption: "El rincón botánico que transforma mis noches.",
  aspectClass: "md:col-span-5 aspect-[4/5]"
}, {
  id: 4,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/community_tincture_hand.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/community_tincture_hand.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796619/img/community_tincture_hand.png-wvc-sizes",
  alt: "Mano sosteniendo el gotero de tintura ámbar de hongos funcionales con luz natural",
  handle: "@rodrigo.menteclara",
  location: "Monterrey",
  caption: "Gotas de Reishi para un descanso verdaderamente reparador.",
  aspectClass: "md:col-span-7 aspect-[4/3] md:aspect-[16/10]"
}];
export default function Community() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section id="comunidad-spora" data-nav="light" className="bg-background text-foreground py-16 md:py-24 lg:py-28 overflow-hidden transition-colors">
      <div className="container mx-auto px-5 sm:px-8 lg:px-14 max-w-[1240px]">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 md:pb-16 border-b border-border/70">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent text-accent-foreground text-xs font-semibold tracking-wider uppercase mb-4 border border-primary/20">
              <Sparkles className="size-3.5 text-primary" />
              <span>@spora.bienestar</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground leading-[1.2]">
              Momentos en comunidad
            </h2>
            <p className="mt-3 text-muted-foreground text-base sm:text-lg leading-relaxed max-w-xl">
              Personas reales viviendo sus propios rituales con hongos funcionales. Comparte tu experiencia usando <span className="font-medium text-foreground">#ComunidadSpora</span>.
            </p>
          </div>

          <div className="shrink-0 pt-2 md:pt-0">
            <Button asChild variant="outline" className="rounded-full px-6 py-5 border-border bg-card hover:bg-secondary text-foreground hover:text-foreground transition-colors group">
              <Link to="https://www.instagram.com/sporabeauty/" newTab aria-label="Síguenos en Instagram @spora.bienestar" className="inline-flex items-center gap-2 text-sm font-semibold">
                <Instagram className="size-4 text-primary" />
                <span>Seguir en Instagram</span>
                <ArrowUpRight className="size-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Asymmetric UGC Gallery Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-12 gap-5 sm:gap-6 lg:gap-8 pt-10 sm:pt-12">
          {galleryMoments.map((item, i) => <motion.article key={item.id} data-index={i} variants={itemVariants} className={`group relative overflow-hidden rounded-2xl sm:rounded-3xl bg-secondary/40 border border-border/80 shadow-xs hover:shadow-md transition-[box-shadow,border-color] duration-300 ${item.aspectClass}`}>
              <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover object-center transform transition-transform duration-700 ease-out group-hover:scale-[1.03]" />

              {/* Permanent gentle vignette on bottom for legibility */}
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent opacity-60 group-hover:opacity-85 transition-opacity duration-300 pointer-events-none" />

              {/* Interactive Handle Overlay Content */}
              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 lg:p-7 flex flex-col justify-end text-background">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center size-8 rounded-full bg-background/20 backdrop-blur-md text-background border border-background/20">
                      <Instagram className="size-3.5 text-background" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold tracking-wide text-background">
                        {item.handle}
                      </p>
                      <p className="text-xs text-background/80 font-normal">
                        {item.location}
                      </p>
                    </div>
                  </div>

                  <span className="hidden sm:inline-flex items-center text-xs font-medium text-background/90 px-2.5 py-1 rounded-full bg-background/15 backdrop-blur-sm border border-background/20 opacity-0 transform translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                    Ver ritual
                  </span>
                </div>

                <p className="mt-2.5 text-xs sm:text-sm text-background/90 font-normal leading-relaxed line-clamp-2 max-w-md">
                  {item.caption}
                </p>
              </div>
            </motion.article>)}
        </motion.div>

        {/* Community Note Footer */}
        <div className="mt-12 sm:mt-16 text-center">
          <div className="inline-flex items-center justify-center gap-3 px-5 py-2.5 rounded-full bg-card border border-border/80 text-muted-foreground text-xs sm:text-sm shadow-2xs">
            <span className="size-2 rounded-full bg-primary animate-pulse" />
            <span>¿Usas Spora en tu rutina? Etiquétanos para aparecer en nuestra galería.</span>
          </div>
        </div>
      </div>
    </section>;
}